// Shared practice-area content for LegalConnect360.
export const PRACTICE_AREAS = [
  {
    slug: "ssdi-leads",
    name: "Social Security Disability",
    navName: "Social Security Disability",
    cardBlurb:
      "SSDI/SSI claimants: initial applications, denials, appeals",
    h1: "Social Security Disability Leads",
    image: "https://images.unsplash.com/photo-1758691031749-607d43c14f63?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "law-firm",
    paragraphs: [
      "LegalConnect360 delivers exclusive Social Security Disability leads for firms handling SSDI and SSI matters. Every lead represents a claimant actively seeking representation — whether they are preparing an initial application, responding to a denial, or pursuing an appeal through reconsideration or a hearing before an administrative law judge.",
      "These leads are best suited for disability practices and firms that manage claimant intake at volume. Prospects are sourced through paid search, compliant social campaigns, and owned web properties built around disability benefits topics. Each submission is captured with documented consent and routed to a single firm in real time.",
      "Because timing matters in disability work, leads are screened to reflect current benefit status and representation, so your intake team spends its time on claimants who can actually move forward.",
    ],
    screenFor: [
      "Work history and recent employment",
      "Current benefit status (initial, denial, or appeal stage)",
      "Whether the claimant already has representation",
      "Age and eligibility indicators",
    ],
  },
  {
    slug: "motor-vehicle-accident-leads",
    name: "Motor Vehicle Accidents",
    navName: "Motor Vehicle Accidents",
    cardBlurb:
      "Auto, truck, motorcycle collision claims with verified accident dates",
    h1: "Motor Vehicle Accident Leads",
    image: "https://images.unsplash.com/photo-1713623311317-d3c43a4be4cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "law-firm",
    paragraphs: [
      "LegalConnect360 provides exclusive motor vehicle accident leads for personal injury firms handling auto, truck, and motorcycle collision claims. Each lead comes from an injured party actively looking for representation following a documented crash.",
      "Leads are generated through paid search, social campaigns, and owned web properties focused on accident and injury topics. Every submission is verified for a recent accident date and delivered to a single firm the moment it is captured, so your team can reach the claimant while the matter is fresh.",
      "This is a high-intent, time-sensitive lead type. We screen for the details that determine whether a claim is viable — injury treatment, fault position, and where the prospect sits relative to the statute of limitations.",
    ],
    screenFor: [
      "Accident date",
      "Injury treatment received",
      "Fault position",
      "Statute of limitations window",
      "Current representation status",
    ],
  },
  {
    slug: "mass-tort-leads",
    name: "Mass Tort",
    navName: "Mass Tort",
    cardBlurb:
      "Campaign-based claimant acquisition with documentation built in",
    h1: "Mass Tort Leads",
    image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "law-firm",
    paragraphs: [
      "LegalConnect360 runs campaign-based claimant acquisition for active mass tort litigation. We build and manage the intake funnel for a specific campaign, then deliver qualified claimants with the supporting documentation your case demands.",
      "Prospects are sourced through paid search, social, and owned web properties targeted to each campaign's qualifying criteria. Because mass tort intake depends on documentation, authorization and case-specific screening are built into the process rather than bolted on afterward.",
      "Mass tort engagements are priced per signed retainer rather than per raw lead, aligning our incentives with the claimants who actually move forward into your case.",
    ],
    screenFor: [
      "Diagnosis confirmation",
      "Treatment timeline",
      "Usage or exposure window",
      "Signed authorization",
    ],
  },
  {
    slug: "solar-panel-fraud-leads",
    name: "Solar Panel Fraud",
    navName: "Solar Panel Fraud",
    cardBlurb:
      "Homeowners misled on solar financing, performance, or contract terms",
    h1: "Solar Panel Fraud Leads",
    image: "https://images.unsplash.com/photo-1660330589505-9a433a742a7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "law-firm",
    paragraphs: [
      "LegalConnect360 delivers exclusive solar panel fraud leads for firms representing homeowners who were misled during the sale, financing, or installation of residential solar systems. These matters typically involve misrepresented savings, undisclosed lender terms, or systems that never performed as promised.",
      "Leads are generated through paid search, social campaigns, and owned web properties built around consumer solar complaints. Every prospect is a verified homeowner and is delivered to a single firm in real time.",
      "We screen for the facts that separate a genuine misrepresentation claim from ordinary buyer's remorse — install date, financing structure, and documented statements made at the point of sale.",
    ],
    screenFor: [
      "Install date",
      "Lender or financing type",
      "Documented misrepresentation",
      "Homeowner status",
    ],
  },
  {
    slug: "bankruptcy-leads",
    name: "Bankruptcy",
    navName: "Bankruptcy",
    cardBlurb:
      "Chapter 7 and 13 prospects screened for debt load and filing intent",
    h1: "Bankruptcy Leads",
    image: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "law-firm",
    paragraphs: [
      "LegalConnect360 provides exclusive bankruptcy leads for consumer bankruptcy practices handling Chapter 7 and Chapter 13 filings. Each lead is a prospect who is actively considering filing and looking for an attorney to guide them.",
      "Prospects are sourced through paid search, social campaigns, and owned web properties focused on debt relief and bankruptcy topics. Every lead is delivered to a single firm in real time so you can begin the consultation while intent is high.",
      "To keep your intake efficient, we screen for the factors that indicate a filing is realistic and imminent — total unsecured debt, income range, prior filings, and stated intent to move forward.",
    ],
    screenFor: [
      "Total unsecured debt",
      "Income range",
      "Prior filings",
      "Filing intent",
    ],
  },
  {
    slug: "fica-tip-credit-leads",
    name: "FICA Tip Credits",
    navName: "FICA Tip Credits",
    cardBlurb:
      "Restaurant and hospitality operators eligible for the 45B tip credit",
    h1: "FICA Tip Credit Leads",
    image: "https://images.unsplash.com/photo-1779591211588-f0302902a53f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "tax-advisory",
    paragraphs: [
      "LegalConnect360 generates FICA tip credit leads for tax professionals, accounting firms, and advisory practices — not consumer attorneys. These leads are restaurant and hospitality business owners who may be eligible to claim the Section 45B credit for the employer share of FICA taxes paid on employee tip income.",
      "Prospects are sourced through paid search, social campaigns, and owned web properties aimed at restaurant and hospitality operators. Each lead is a business decision-maker delivered to a single firm in real time, ready for an advisory conversation about eligibility and potential credit recovery.",
      "Because 45B eligibility depends on entity and payroll specifics, we screen upfront for the operational details your advisory team needs to assess the opportunity quickly.",
    ],
    screenFor: [
      "Entity type",
      "Payroll provider",
      "Tipped employee count",
      "Years in operation",
    ],
  },
  {
    slug: "tariff-refunds-leads",
    name: "Tariff Refunds",
    navName: "Tariff Refunds",
    cardBlurb:
      "Importers and businesses eligible to recover overpaid customs duties and tariffs",
    h1: "Tariff Refund Leads",
    image: "https://images.unsplash.com/photo-1670121180530-cfcba4438038?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    audience: "tax-advisory",
    paragraphs: [
      "LegalConnect360 generates tariff refund leads for customs and trade advisory firms, accounting practices, and law firms with international trade groups — not consumer attorneys. These leads are importers and businesses that may be entitled to recover overpaid customs duties and tariffs through mechanisms such as duty drawback, tariff misclassification corrections, and Section 301 exclusion refunds.",
      "Prospects are sourced through paid search, social campaigns, and owned web properties aimed at importers, manufacturers, and supply-chain decision-makers. Each lead is a business contact delivered to a single firm in real time, ready for a conversation about eligibility and potential refund recovery.",
      "Because refund eligibility depends on import history and product classification, we screen upfront for the operational details your advisory team needs to size the opportunity quickly.",
    ],
    screenFor: [
      "Annual import volume and duties paid",
      "Product categories and HTS classification",
      "Timeframe of duties paid (refund lookback window)",
      "Prior refund or drawback claims filed",
    ],
  },
];

export const PRACTICE_AREA_OPTIONS = [
  ...PRACTICE_AREAS.map((a) => a.name),
  "Other",
];

export function getPracticeArea(slug) {
  return PRACTICE_AREAS.find((a) => a.slug === slug);
}
